/**
 * The small shared pieces. Deliberately few: this app has one accent colour, one badge shape and
 * one way of showing a number with a label, and every screen uses those instead of inventing more.
 */
import type { ReactNode } from "react";
import type { Flag } from "../../../server/types.ts";
import { FLAG_COPY } from "../format.ts";
import { Icon } from "./icons.tsx";

export type Tone = "neutral" | "accent" | "money" | "warn" | "danger";

export function Badge({ tone = "neutral", children }: { tone?: Tone | undefined; children: ReactNode }) {
	return <span className={`badge badge-${tone}`}>{children}</span>;
}

export function Stat({ label, value, hint }: { label: string; value: ReactNode; hint?: string | undefined }) {
	return (
		<div className="stat">
			<span className="stat-label">{label}</span>
			<span className="stat-value">{value}</span>
			{hint !== undefined && <span className="stat-hint">{hint}</span>}
		</div>
	);
}

export function Note({ tone = "neutral", children }: { tone?: Tone | undefined; children: ReactNode }) {
	return (
		<p className={`note note-${tone}`}>
			<Icon name={tone === "warn" || tone === "danger" ? "alert" : "info"} />
			<span>{children}</span>
		</p>
	);
}

export function Spinner({ label }: { label: string }) {
	return (
		<span className="spinner" role="status">
			<i />
			{label}
		</span>
	);
}

/** Activation slots. `K` is small and countable, so count it rather than printing a fraction. */
export function Slots({ used, free }: { used: number; free: number }) {
	const total = used + free;
	return (
		<span className="slots" aria-label={`${used} of ${total} activation slots used`}>
			{Array.from({ length: total }, (_, index) => (
				<i key={index} className={index < used ? "slot slot-on" : "slot"} />
			))}
			{total === 0 && <span className="slots-empty">no slots</span>}
		</span>
	);
}

export function Meter({ value, tone = "accent" }: { value: number; tone?: Tone | undefined }) {
	const clamped = Math.max(0, Math.min(1, value));
	return (
		<span className="meter" role="meter" aria-valuenow={Math.round(clamped * 100)} aria-valuemin={0} aria-valuemax={100}>
			<i className={`meter-fill meter-${tone}`} style={{ width: `${clamped * 100}%` }} />
		</span>
	);
}

/**
 * Confidence flags, spelled out. ALGORITHM.md §16 asks for these to reach the user, and a bare
 * `APPROX_VALUATION` chip would not: the detail line is the part that changes a decision.
 */
export function Flags({ flags }: { flags: Flag[] }) {
	if (flags.length === 0) {
		return (
			<p className="flags-clean">
				<Icon name="check" /> Nothing to disclaim: prices, boxes and coverage were all complete.
			</p>
		);
	}
	return (
		<ul className="flags">
			{flags.map((flag) => {
				const copy = FLAG_COPY[flag];
				return (
					<li key={flag} className={`flag flag-${copy.severity}`}>
						<span className="flag-head">
							<Icon name={copy.severity === "warn" ? "alert" : "info"} />
							{copy.label}
							<code>{flag}</code>
						</span>
						<span className="flag-detail">{copy.detail}</span>
					</li>
				);
			})}
		</ul>
	);
}

export function Empty({ title, children }: { title: string; children: ReactNode }) {
	return (
		<div className="empty">
			<h2>{title}</h2>
			<p>{children}</p>
		</div>
	);
}
